// ============================================================================
// The work handoff is a SCREEN, not a bulk assignment.
//
// ⛔ Handing the work-handoff group to the National AM gives him the ASSIGNMENT
// TOOL for 48 facilities and 35 route stops. It does not make him the account
// manager for 48 facilities. The app proposes the lowest-loaded active manager
// in each market; he accepts or overrides each one, row by row. Nothing
// auto-commits a book of business.
//
// "Leave unassigned" is permitted and it is BLOCKING — an unowned facility is a
// silent service gap, and pretending otherwise is how cleaners end up with
// nobody to call.
// ============================================================================

import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button, Card, useToast } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import type { ChecklistItem, SubStep } from './types';
import {
  completeItem,
  setSubSteps,
  useEmployeePicker,
} from './hooks/use-separation-data';

export interface HandoffScreenProps {
  item: ChecklistItem;
  names: Map<string, string>;
  onBack: () => void;
  onChanged: () => void;
}

export function HandoffScreen({ item, names, onBack, onChanged }: HandoffScreenProps) {
  const toast = useToast();
  const { people } = useEmployeePicker();
  const [rows, setRows] = useState<SubStep[]>(
    Array.isArray(item.sub_steps) ? item.sub_steps.filter((s) => !!s.ref) : [],
  );
  const [busy, setBusy] = useState(false);

  const byMarket = useMemo(() => {
    const groups = new Map<string, Array<{ row: SubStep; index: number }>>();
    rows.forEach((row, index) => {
      const key = row.market ?? 'Unassigned market';
      const list = groups.get(key) ?? [];
      list.push({ row, index });
      groups.set(key, list);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const accepted = rows.filter((r) => r.accepted && r.proposed_eid).length;
  const unassigned = rows.filter((r) => !r.proposed_eid).length;

  function setRow(index: number, patch: Partial<SubStep>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function acceptAllInMarket(market: string) {
    setRows((prev) =>
      prev.map((r) =>
        (r.market ?? 'Unassigned market') === market && r.proposed_eid
          ? { ...r, accepted: true }
          : r,
      ),
    );
  }

  async function save(alsoApply: boolean) {
    setBusy(true);
    try {
      await setSubSteps(item.item_id, rows);
      if (alsoApply) {
        if (unassigned > 0) {
          toast.error('Every row needs a named owner', {
            description: `${unassigned} still has nobody. An unowned facility is a silent service gap, so this stays blocking until it has one.`,
          });
          return;
        }
        await completeItem(item.item_id, `${accepted} accepted.`);
        toast.success('Handoff recorded', {
          description: `${accepted} row(s) accepted. They are applied on the last working day.`,
        });
      } else {
        toast.success('Decisions saved');
      }
      onChanged();
    } catch (err) {
      toast.error("Couldn't save", { description: errorMessage(err, 'Try again.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={onBack}>
          Back to the checklist
        </Button>
        <div className="text-sm text-tcs-text-secondary">
          {accepted} of {rows.length} accepted
          {unassigned > 0 && (
            <span className="ml-2 text-sentiment-critical-text">
              · {unassigned} with no owner
            </span>
          )}
        </div>
      </div>

      <Card className="p-4">
        <h2 className="text-base font-semibold text-tcs-text">{item.label}</h2>
        <p className="mt-1 text-sm text-tcs-text-secondary">
          The app proposes the lowest-loaded active manager in each market. You accept or
          override each one — nothing is committed until you do.
        </p>
      </Card>

      {byMarket.map(([market, entries]) => (
        <Card key={market} className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tcs-border pb-2">
            <h3 className="text-sm font-semibold text-tcs-text">
              {market} <span className="text-tcs-text-muted">· {entries.length}</span>
            </h3>
            <Button size="sm" variant="secondary" onClick={() => acceptAllInMarket(market)}>
              Accept every proposal in {market}
            </Button>
          </div>
          <ul className="divide-y divide-tcs-border">
            {entries.map(({ row, index }) => (
              <li key={row.ref} className="flex flex-wrap items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-tcs-text">{row.label ?? row.ref}</div>
                  <div className="text-xs text-tcs-text-muted">
                    {row.ref}
                    {row.role_on_card ? ` · they are the ${row.role_on_card}` : ''}
                  </div>
                </div>
                <select
                  value={row.proposed_eid ?? ''}
                  onChange={(e) =>
                    setRow(index, { proposed_eid: e.target.value || null, accepted: false })
                  }
                  className="w-56 rounded border border-tcs-border px-2 py-1 text-sm"
                  aria-label={`New owner for ${row.label ?? row.ref}`}
                >
                  <option value="">Leave unassigned — blocking</option>
                  {people.map((p) => (
                    <option key={p.employee_id} value={p.employee_id}>
                      {p.display_name ?? p.employee_id}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-tcs-text-secondary">
                  <input
                    type="checkbox"
                    checked={!!row.accepted}
                    disabled={!row.proposed_eid}
                    onChange={(e) => setRow(index, { accepted: e.target.checked })}
                  />
                  Accept
                </label>
                {row.proposed_eid && !row.accepted && (
                  <span className="text-xs text-tcs-text-muted">
                    proposed: {names.get(row.proposed_eid) ?? row.proposed_eid}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ))}

      {rows.length === 0 && (
        <Card className="p-6 text-sm text-tcs-text-secondary">
          Nothing staged yet. The proposals appear once the release cascade has run for
          this separation.
        </Card>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" loading={busy} onClick={() => void save(false)}>
          Save my decisions
        </Button>
        <Button loading={busy} disabled={rows.length === 0} onClick={() => void save(true)}>
          Accept and mark the handoff done
        </Button>
      </div>
    </div>
  );
}
